import React, { useEffect, useState } from 'react';
import { Image } from 'antd';
import { useParams } from 'react-router-dom';
import { isFunctionalityEnabled } from '../../../../config/functionalityAvailability';
import { UPLOAD_PHOTOS } from '../../../../config/paramsConfig';
import { NavigationKey } from '../../../components/appNavigationBar/AppNavigation.types';
import PageHeader from '../../../components/common/pageHeader/PageHeader';
import ProductItemHeader from '../../../components/favoritProducts/favoriteProductItem/productItemHeader/ProductItemHeader';
import ProductComments from '../../../components/productInfo/comments/ProductComments';
import ProductPricing from '../../../components/productInfo/pricing/PricingInfo';
import ProductUploads, { UploadType } from '../../../components/productInfo/uploads/UploadsInfo';
import { flattenTcMfSectionProducts } from '../../../helpers/tcExternalProducts';
import { toGuidelineInternalProduct } from '../../../helpers/liveGuidelineProducts';
import { mergeInternalProductsWithExternal } from '../../../helpers/mergeProjectProducts';
import HelpModal from '../../../components/helpModal/HelpModal';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { useModalState } from '../../../hooks/modalState';
import { useUploadDocumentTypes } from '../../../hooks/uploadDocumentTypes';
import { ActionResponse } from '../../../models/actionResponse/types';
import { AccountPermissionTypes, ProjectPermissionTypes } from '../../../models/permission/enums';
import { BrandCompany, ExternalProduct, Product } from '../../../models/product/types';
import { Division, Section } from '../../../models/productDivision/types';
import { MFRProject } from '../../../models/project/types';
import {
    getLiveGuidelineByIdAction,
    getLiveGuidelineProductsAction,
} from '../../../redux/actions/liveGuidelineActions';
import {
    LiveGuidelineDetail,
    LiveGuidelineProduct,
} from '../../../redux/actions/liveGuidelineActions/types';
import { getProductReferencesAction } from '../../../redux/actions/productActions';
import { ProductReferences } from '../../../redux/actions/productActions/types';
import { getCompaniesOfProducts, getExternalProductsAction } from '../../../redux/actions/transparencyCatalogActions';
import { GetProductsFromTransparencyCatalogAPIResponse } from '../../../redux/actions/transparencyCatalogActions/types';
import { addPages, removePages } from '../../../redux/reducers/navigationReducer/NavigationSlice';
import { blue01, blueDark01, blueLink } from '../../../styles/colors';
import { EmptyPendingPage, MiddleSpin, Text } from '../../../styles/common.styles';
import {
    MFRPageContainer,
    MFRPageContent,
    ProductsCount,
    ReturnToProductTabContainer,
    ReturnToProductTabLink,
} from '../../mfrPage/MFRPage.styles';
import MFRDivisionItem from '../../mfrPage/mfrDivision/MFRDivision';
import { MFRPProductItemContainer } from '../../mfrPage/mfrProduct/MFRProduct.styles';
import MFRProductInfo from '../../mfrPage/mfrProduct/mfrProductInfo/MFRProductInfo';
import MFRPageCompanyHeader from '../../mfrPage/MFRPageCompanyHeader';

interface Params {
    id: string;
    liveGuidelineProductId: string;
    mode?: string;
}

const LiveGuidelineProductPage: React.FC = () => {
    const params = useParams<Params>();
    const dispatch = useAppDispatch();
    const user = useAppSelector((state) => state.authReducer.user);
    const { sustainabilityDocuments, leedDocumentTypes } = useUploadDocumentTypes();

    const liveGuidelineId = Number(params.id);
    const liveGuidelineProductId = Number(params.liveGuidelineProductId);

    const [guideline, setGuideline] = useState<LiveGuidelineDetail | null>(null);
    const [divisions, setDivisions] = useState<Array<Division<Product>>>([]);
    const [productsCount, setProductsCount] = useState(0);
    const [references, setReferences] = useState<ProductReferences | null>(null);
    const [brandInformation, setBrandInformation] = useState<BrandCompany | undefined>(undefined);
    const { isOpen: isHelpOpen, onToggle: toggleHelp } = useModalState();
    const [pending, setPending] = useState(true);

    useEffect(() => {
        loadData();
    }, [liveGuidelineId, liveGuidelineProductId]);

    useEffect(() => {
        return () => {
            dispatch(removePages());
        };
    }, []);

    useEffect(() => {
        dispatch(
            addPages([
                { key: NavigationKey.DASHBOARD, value: `${user?.firstName}’s Dashboard` },
                { key: NavigationKey.LIVE_GUIDELINES, value: 'Live Guidelines' },
                {
                    key: `/live-guidelines/${liveGuidelineId}`,
                    value: guideline?.name || '',
                    image: 'src/assets/images/lightning-charge-fill.svg',
                },
                {
                    key: `/live-guidelines/${liveGuidelineId}/product/${liveGuidelineProductId}`,
                    value: brandInformation?.company
                        ? `${brandInformation.company} product list`
                        : '',
                },
            ]),
        );
    }, [guideline?.name, brandInformation?.company]);

    const loadData = async () => {
        setPending(true);
        try {
            const [guidelineResult, productsResult, referencesResult] = (await Promise.all([
                dispatch(getLiveGuidelineByIdAction(liveGuidelineId)),
                dispatch(getLiveGuidelineProductsAction(liveGuidelineId)),
                dispatch(getProductReferencesAction()),
            ])) as unknown as [
                ActionResponse<LiveGuidelineDetail>,
                ActionResponse<LiveGuidelineProduct[]>,
                ActionResponse<ProductReferences>,
            ];
            setGuideline(guidelineResult.payload?.data || null);
            setReferences(referencesResult.payload?.data ?? null);

            const guidelineProducts = (productsResult.payload?.data || []).filter(
                (p) => p.productId != null,
            );
            if (!guidelineProducts.some((p) => p.id === liveGuidelineProductId)) {
                setDivisions([]);
                return;
            }

            const { payload } = (await dispatch(
                getExternalProductsAction({ productIds: guidelineProducts.map((p) => p.productId!) }),
            )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;
            const external = payload?.data ? flattenTcMfSectionProducts(payload.data.mfSection) : [];
            const merged = mergeInternalProductsWithExternal(
                guidelineProducts.map(toGuidelineInternalProduct),
                external,
            );
            const selected = merged.find((p) => p.id === liveGuidelineProductId);
            if (!selected) {
                setDivisions([]);
                return;
            }

            // Same as the project page: list only the selected product's manufacturer.
            const productsByCompany = merged.filter((p) => p.companyID === selected.companyID);
            const [company] = await getCompaniesOfProducts(
                [selected as unknown as ExternalProduct],
                dispatch,
            );
            productsByCompany.forEach((p) => {
                const specSection = (company as any)?.specs?.find(
                    (spec: any) => spec.mfSectionNumber === p.mfSection?.number,
                );
                if (specSection) {
                    p.specUrl = specSection.specUrl;
                }
            });
            setBrandInformation(company);

            const builtDivisions = buildDivisions(productsByCompany);
            openSelectedProduct(selected, builtDivisions);
            setDivisions(builtDivisions);
            setProductsCount(productsByCompany.length);
        } finally {
            setPending(false);
        }
    };

    const buildDivisions = (products: Product[]): Array<Division<Product>> => {
        const divisionsMap = new Map<number, any>();
        products.forEach((product) => {
            const key = parseInt(product.mfDivision.number[0] + product.mfDivision.number[1]);
            const highestSection = product.mfSectionLevel1 || product.mfSection;
            const sectionId = highestSection.number.slice(0, 5);
            if (!divisionsMap.has(key)) {
                divisionsMap.set(key, {
                    id: product.mfDivision.number,
                    name: product.mfDivision.label,
                    key,
                    sections: new Map(),
                    clicked: false,
                });
            }
            const sections = divisionsMap.get(key).sections;
            if (!sections.has(sectionId)) {
                sections.set(sectionId, {
                    id: sectionId,
                    key: parseInt(highestSection.number[3] + highestSection.number[4]),
                    name: highestSection.label,
                    clicked: false,
                    products: [],
                });
            }
            sections.get(sectionId).products.push(product);
        });

        return [...divisionsMap.values()]
            .map((division) => ({
                ...division,
                sections: [...division.sections.values()].sort(
                    (a: Section<Product>, b: Section<Product>) => a.key - b.key,
                ),
            }))
            .sort((a, b) => a.key - b.key);
    };

    const openSelectedProduct = (product: Product, builtDivisions: Array<Division<Product>>) => {
        const division = builtDivisions.find((d) => d.id === product.mfDivision.number);
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        const section = division?.sections.find((s) => s.id === highestSection.number.slice(0, 5));
        if (division && section) {
            division.clicked = true;
            section.clicked = true;
            setTimeout(() => {
                document.getElementById(`${liveGuidelineProductId}`)?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                });
            }, 500);
        }
    };

    const handleDivisionOpen = (id: string) => {
        setDivisions((current) =>
            current.map((division) =>
                division.id === id ? { ...division, clicked: !division.clicked } : division,
            ),
        );
    };

    const handleSectionOpen = (divisionId: string, sectionId: string) => {
        setDivisions((current) =>
            current.map((division) =>
                division.id === divisionId
                    ? {
                          ...division,
                          sections: division.sections.map((section) =>
                              section.id === sectionId
                                  ? { ...section, clicked: !section.clicked }
                                  : section,
                          ),
                      }
                    : division,
            ),
        );
    };

    // The upload components derive edit/delete rights from a project role: a guideline owner or
    // Account Admin maps to OWNER (full), any other member to COMMENTER (add only), as the API enforces.
    const canEdit =
        user?.accountRole === AccountPermissionTypes.ACCOUNT_ADMIN ||
        !!guideline?.owners.some((owner) => owner.userId === user?.id);
    const permissionContext = {
        userRole: canEdit ? ProjectPermissionTypes.OWNER : ProjectPermissionTypes.COMMENTER,
    } as MFRProject;

    if (pending) {
        return (
            <MiddleSpin spinning size="large">
                <EmptyPendingPage />
            </MiddleSpin>
        );
    }

    const returnLink = `/live-guidelines/${liveGuidelineId}/1`;
    const showHelp =
        brandInformation?.contactEmail !== null && brandInformation?.contactInfo !== null;

    const renderProduct = (product: Product, index: number) => {
        // Only the product the user clicked opens on the requested panel (comments/documents/images).
        const isSelected = product.id === liveGuidelineProductId;
        const sameSectionCount = divisions
            .flatMap((division) => division.sections)
            .flatMap((section) => section.products)
            .filter((p) => p.mfSection.number === product.mfSection.number).length;
        return (
            <MFRPProductItemContainer id={product.id.toString()}>
                <ProductItemHeader
                    product={product}
                    productsCount={sameSectionCount}
                    showCheckbox={false}
                    showHelp={showHelp}
                    showHeader={index === 0}
                />
                <MFRProductInfo
                    references={references}
                    project={permissionContext}
                    product={product}
                    editable={canEdit}
                    projectRoles={[]}
                />
                <ProductComments
                    product={product}
                    selectedProductId={product.productId.toString()}
                    openByDefault={isSelected && params.mode === 'comments'}
                />
                <ProductPricing product={product} userRole={permissionContext.userRole} />
                <ProductUploads
                    teamMembers={[]}
                    docAndImagesUploads={[]}
                    newSustainabilityDocuments={sustainabilityDocuments}
                    leedDocumentTypes={leedDocumentTypes}
                    allDocumentTypes={references?.allDocumentTypes || []}
                    product={product}
                    project={permissionContext}
                    openByDefault={isSelected && params.mode === 'documents'}
                    uploadType={UploadType.DOCUMENT}
                />
                {isFunctionalityEnabled(UPLOAD_PHOTOS) && (
                    <ProductUploads
                        teamMembers={[]}
                        docAndImagesUploads={[]}
                        newSustainabilityDocuments={sustainabilityDocuments}
                        leedDocumentTypes={leedDocumentTypes}
                        allDocumentTypes={references?.allDocumentTypes || []}
                        product={product}
                        project={permissionContext}
                        openByDefault={isSelected && params.mode === 'images'}
                        uploadType={UploadType.PICTURE}
                    />
                )}
            </MFRPProductItemContainer>
        );
    };

    return (
        <>
            <PageHeader
                title={guideline?.name || ''}
                iconTitleGap={4}
                iconProp={
                    guideline?.name
                        ? { src: 'lightning-charge-fill.svg', style: { width: 24, height: 24 } }
                        : undefined
                }
            />
            <MFRPageContainer>
                <ReturnToProductTabContainer>
                    <ReturnToProductTabLink to={returnLink}>
                        <Image src="src/assets/images/return-arrow.svg" preview={false} />
                        <Text weight={400} height={18} size={14} color={blueLink}>
                            Return to Products tab
                        </Text>
                    </ReturnToProductTabLink>
                </ReturnToProductTabContainer>
                <MFRPageCompanyHeader
                    company={brandInformation}
                    onToggle={toggleHelp}
                    user={user}
                />
                {divisions.length > 0 ? (
                    <>
                        <ProductsCount color={blue01} weight={600} height={24} size={20}>
                            Products in this Live Guideline ({productsCount})
                        </ProductsCount>
                        <MFRPageContent>
                            {divisions.map((division) => (
                                <MFRDivisionItem
                                    key={division.id}
                                    division={division}
                                    handleClicked={handleDivisionOpen}
                                    handleSectionOpen={handleSectionOpen}
                                    references={references}
                                    projectData={permissionContext}
                                    editable={false}
                                    showHelp={showHelp}
                                    teamMembers={[]}
                                    projectRoles={[]}
                                    docAndImagesUploads={[]}
                                    renderProduct={renderProduct}
                                />
                            ))}
                        </MFRPageContent>
                    </>
                ) : (
                    <Text weight={400} height={18} size={14} color={blueDark01}>
                        This product is no longer available in the Live Guideline.
                    </Text>
                )}
            </MFRPageContainer>
            {isHelpOpen && (
                <HelpModal
                    isOpen={isHelpOpen}
                    handleCancel={toggleHelp}
                    companyId={brandInformation?.id || 0}
                />
            )}
        </>
    );
};

export default LiveGuidelineProductPage;
